import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { NativeRecall } from '../components/NativeRecall'
import { SmoothDecodedText } from '../components/SmoothDecodedText'
import { nativeSoftDecodeActiveBlock } from '../components/SoftDecodedText'
import '../native-chat-stream.css'
import { authFetch } from '../lib/auth'
import { renderMarkdown } from '../lib/markdown'
import { readSse, type NativeEvent } from '../lib/sse'
import { isActiveLifecycle, nativeActivityLabel, proactiveAssistantTurnId, terminalLifecycleStatus } from '../lib/nativeActivity'
import { buildNativeUsageSnapshot, type NativeUsageSnapshot } from '../lib/nativeUsage'
import { drainScheduledNativeFrame, settleScheduledNativeFrameBeforeFailure } from '../lib/nativeStreamFrame'
import { applyNativeStreamEvent, emptyNativeTurnStream, nativeEventBelongsToTurn, type NativeContentSegment, type NativeRecallItem } from '../lib/nativeTurn'
import { mergeNativeHistoryDiagnostics, nativeDiagnosticSummary, type NativeDiagnosticRecord, type NativeHistoryRecord } from '../lib/nativeTimeline'

interface Props { active: boolean; onUserActivity: () => void; onUsage: (usage: NativeUsageSnapshot) => void }
interface Message {
  key: string
  id?: number
  role: 'user' | 'assistant'
  content: string
  thinking?: string | null
  recall?: NativeRecallItem[]
  segments?: NativeContentSegment[] | null
  turnId?: string | null
  ts?: string
  error?: boolean
  errorSummary?: string
  errorMessage?: string | null
  providerMessage?: string | null
  visibleText?: string | null
}
type HistoryMessage = NativeHistoryRecord

const MAX_TEXTAREA_HEIGHT = 120

function NativeErrorCard({ message }: { message: Message }) {
  const details = [message.errorMessage, message.providerMessage, message.visibleText]
    .filter((value, index, values): value is string => Boolean(value) && values.indexOf(value) === index)
  return <div className="native-error-card" role="status">
    <div className="native-error-title">⚠️ {message.errorSummary || 'Turn failed'}</div>
    {details.length > 0 && <details className="native-error-details"><summary>详情</summary>
      {details.map((detail, index) => <pre key={index}>{detail}</pre>)}
    </details>}
  </div>
}

function NativeOrderedSegments({ segments, streaming, onStreamDisplayChange }: { segments: NativeContentSegment[]; streaming: boolean; onStreamDisplayChange: () => void }) {
  const lastIndex = segments.length - 1
  return <>
    {segments.map((segment, index) => {
      const activeSegment = streaming && index === lastIndex
      if (segment.kind === 'thinking') {
        return <div className="chat-msg thinking" key={`thinking-${index}`}>
          <details className="msg-thinking" open={activeSegment}>
            <summary>思考</summary>
            <div className="msg-thinking-content"><SmoothDecodedText text={segment.content} active={activeSegment} onVisibleChange={onStreamDisplayChange} /></div>
          </details>
        </div>
      }
      return <div className={`chat-msg assistant${activeSegment ? ' streaming' : ''}`} key={`text-${index}`}>
        {streaming
          ? <div className="native-streaming-text"><SmoothDecodedText text={segment.content} active={activeSegment} onVisibleChange={onStreamDisplayChange} /></div>
          : <div dangerouslySetInnerHTML={{ __html: renderMarkdown(segment.content) }} />}
      </div>
    })}
  </>
}

const NativeMessageRow = memo(function NativeMessageRow({ message, streaming, onStreamDisplayChange }: { message: Message; streaming: boolean; onStreamDisplayChange: () => void }) {
  if (message.error) return <div className="native-message-row"><NativeErrorCard message={message} /></div>
  const orderedSegments = message.role === 'assistant' && Array.isArray(message.segments)
    ? message.segments.filter((segment) => segment.content)
    : []
  const activeDecodeBlock = nativeSoftDecodeActiveBlock(streaming, message.thinking || '', message.content)
  const thinkingStreaming = activeDecodeBlock === 'thinking'
  const answerStreaming = activeDecodeBlock === 'answer'
  const showAssistantBubble = message.role !== 'assistant' || !streaming || Boolean(message.content) || !message.thinking

  return <div className="native-message-row">
    {message.role === 'assistant' && <NativeRecall items={message.recall || []} />}
    {orderedSegments.length > 0
      ? <NativeOrderedSegments segments={orderedSegments} streaming={streaming} onStreamDisplayChange={onStreamDisplayChange} />
      : <>
        {message.role === 'assistant' && message.thinking && <div className="chat-msg thinking"><details className="msg-thinking" open={streaming}><summary>思考</summary><div className="msg-thinking-content"><SmoothDecodedText text={message.thinking} active={thinkingStreaming} onVisibleChange={onStreamDisplayChange} /></div></details></div>}
        {showAssistantBubble && <div className={`chat-msg ${message.role}${streaming ? ' streaming' : ''}`}>
          {message.role === 'assistant'
            ? streaming
              ? <div className="native-streaming-text"><SmoothDecodedText text={message.content} active={answerStreaming} onVisibleChange={onStreamDisplayChange} /></div>
              : <div dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }} />
            : <div>{message.content}</div>}
        </div>}
      </>}
  </div>
})

export function NativeChatPage({ active, onUserActivity, onUsage }: Props) {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState('offline')
  const [tool, setTool] = useState('')
  const [contextWindowTokens, setContextWindowTokens] = useState<number | null>(null)
  const [lifecycle, setLifecycle] = useState<NativeEvent | null>(null)
  const [historyReady, setHistoryReady] = useState(false)
  const pending = useRef({ turnId: '', stream: emptyNativeTurnStream() })
  const raf = useRef<number | null>(null)
  const historyLoaded = useRef(false)
  const messagesRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const followBottom = useRef(true)
  const messageSequence = useRef(0)
  const proactiveTurnIds = useRef(new Set<string>())
  const turnErrorIds = useRef(new Set<string>())

  const followStreamDisplay = useCallback(() => {
    if (!followBottom.current) return
    const node = messagesRef.current
    if (!node) return
    node.scrollTop = node.scrollHeight
  }, [])

  function nextMessageKey(prefix: string) {
    messageSequence.current += 1
    return `${prefix}-${messageSequence.current}`
  }

  function isNearBottom() {
    const node = messagesRef.current
    if (!node) return true
    return node.scrollHeight - node.scrollTop - node.clientHeight <= 48
  }

  function handleMessagesScroll() {
    followBottom.current = isNearBottom()
  }

  function autoResizeInput(el: HTMLTextAreaElement) {
    el.style.height = 'auto'
    const nextHeight = Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)
    el.style.height = `${nextHeight}px`
    el.style.overflowY = el.scrollHeight > MAX_TEXTAREA_HEIGHT ? 'auto' : 'hidden'
  }

  function handleInputChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setInput(event.target.value)
    autoResizeInput(event.target)
  }

  useEffect(() => {
    if (!active) return
    let cancelled = false
    const controller = new AbortController()

    void authFetch('/api/native-chat/status', { signal: controller.signal }).then(async (r) => {
      if (!r.ok || cancelled) return
      const payload = (await r.json()) as { state?: string; context_window_tokens?: number | null }
      setStatus(payload.state || 'offline')
      setContextWindowTokens(
        typeof payload.context_window_tokens === 'number' && payload.context_window_tokens > 0
          ? payload.context_window_tokens
          : null,
      )
    }).catch(() => undefined)

    const historyTask: Promise<void> = !historyLoaded.current
      ? Promise.all([
          authFetch('/api/native-chat/history?limit=200', { signal: controller.signal }),
          authFetch('/api/native-chat/diagnostics?limit=100', { signal: controller.signal }).catch(() => null),
        ]).then(async ([historyResponse, diagnosticResponse]) => {
          if (!historyResponse.ok) throw new Error(`history HTTP ${historyResponse.status}`)
          const historyPayload = (await historyResponse.json()) as { messages?: HistoryMessage[] }
          const diagnosticPayload = diagnosticResponse?.ok
            ? (await diagnosticResponse.json()) as { diagnostics?: NativeDiagnosticRecord[] }
            : { diagnostics: [] as NativeDiagnosticRecord[] }
          if (cancelled) return
          const history = Array.isArray(historyPayload.messages) ? historyPayload.messages : []
          const diagnostics = Array.isArray(diagnosticPayload.diagnostics) ? diagnosticPayload.diagnostics : []
          const timeline = mergeNativeHistoryDiagnostics(history, diagnostics)
          followBottom.current = true
          setMessages(timeline.map((item, index): Message => {
            if (item.kind === 'message') {
              const message = item.value
              return {
                ...message,
                thinking: message.thinking || undefined,
                segments: message.segments || undefined,
                turnId: message.turnId || undefined,
                key: message.id !== undefined ? `history-${message.id}` : `history-${index}-${message.role}`,
              }
            }
            const diagnostic = item.value
            if (diagnostic.turn_id) turnErrorIds.current.add(diagnostic.turn_id)
            return {
              key: diagnostic.id !== undefined ? `diagnostic-${diagnostic.id}` : `diagnostic-${diagnostic.turn_id}-${index}`,
              role: 'assistant',
              content: '',
              turnId: diagnostic.turn_id,
              ts: diagnostic.ts,
              error: true,
              errorSummary: nativeDiagnosticSummary(diagnostic),
              errorMessage: diagnostic.message,
              providerMessage: diagnostic.provider_message,
              visibleText: diagnostic.visible_text,
            }
          }))
          historyLoaded.current = true
          setHistoryReady(true)
        }).catch(() => { if (!cancelled) setHistoryReady(true) })
      : Promise.resolve().then(() => { if (!cancelled) setHistoryReady(true) })

    async function streamProactiveTurn(turnId: string) {
      if (!turnId || proactiveTurnIds.current.has(turnId) || cancelled) return
      proactiveTurnIds.current.add(turnId)
      followBottom.current = isNearBottom()
      setBusy(true); setStatus('busy'); setTool('')
      pending.current = { turnId, stream: emptyNativeTurnStream() }
      setMessages((prev) => [...prev, { key: `turn-${turnId}`, role: 'assistant', content: '', turnId }])
      try {
        const response = await authFetch(`/api/native-chat/turns/${encodeURIComponent(turnId)}/events`, {
          headers: { Accept: 'text/event-stream' },
          signal: controller.signal,
        })
        if (!response.ok) throw new Error(`proactive turn HTTP ${response.status}`)
        await readSse(response, (event) => consume(event, turnId))
      } catch (error) {
        if (!controller.signal.aborted) {
          settleScheduledNativeFrameBeforeFailure(
            raf, turnId, cancelAnimationFrame, commitPending,
            () => appendTurnError(turnId, 'Proactive turn failed', { errorMessage: error instanceof Error ? error.message : 'Proactive turn stream failed' }),
          )
          setBusy(false); setStatus('error'); setTool('')
        }
      }
    }

    void authFetch('/api/native-chat/lifecycle', {
      headers: { Accept: 'text/event-stream' },
      signal: controller.signal,
    }).then(async (r) => {
      if (!r.ok) throw new Error(`lifecycle HTTP ${r.status}`)
      await readSse(r, (event) => {
        if (cancelled || event.kind !== 'runtime_lifecycle') return
        const proactiveTurnId = proactiveAssistantTurnId(event)
        if (proactiveTurnId) void historyTask.then(() => streamProactiveTurn(proactiveTurnId))
        if (isActiveLifecycle(event)) setLifecycle(event)
        else {
          setLifecycle(null)
          setStatus((current) => terminalLifecycleStatus(event, current))
        }
      })
    }).catch((error) => {
      if (!controller.signal.aborted) console.warn('native lifecycle stream ended', error)
    })

    return () => { cancelled = true; controller.abort() }
  }, [active])

  useLayoutEffect(() => {
    if (!followBottom.current) return
    const node = messagesRef.current
    if (!node) return
    node.scrollTop = node.scrollHeight
  }, [messages, tool, busy])

  function commitPending(turnId: string) {
    if (pending.current.turnId !== turnId) return
    const next = pending.current.stream
    setMessages((prev) => {
      const copy = [...prev]
      let index = -1
      for (let i = copy.length - 1; i >= 0; i -= 1) {
        const candidate = copy[i]
        if (candidate.role === 'assistant' && !candidate.error && (candidate.turnId === turnId || (!candidate.turnId && i === copy.length - 1))) {
          index = i
          break
        }
      }
      const value: Message = {
        key: index >= 0 ? copy[index].key : (turnId ? `turn-${turnId}` : nextMessageKey('assistant')),
        role: 'assistant', content: next.text, thinking: next.thinking, recall: next.recall,
        segments: next.segments, turnId: turnId || undefined,
      }
      if (index >= 0) copy[index] = { ...copy[index], ...value }
      else copy.push(value)
      return copy
    })
  }

  function flush(turnId: string) {
    if (raf.current !== null) return
    raf.current = requestAnimationFrame(() => {
      raf.current = null
      commitPending(turnId)
    })
  }

  function flushNow(turnId: string) {
    drainScheduledNativeFrame(raf, turnId, cancelAnimationFrame, commitPending)
  }

  function appendTurnError(
    turnId: string,
    summary: string,
    details: { errorMessage?: string | null; providerMessage?: string | null; visibleText?: string | null } = {},
  ) {
    if (turnId) turnErrorIds.current.add(turnId)
    setMessages((prev) => {
      const withoutFailedAssistant = prev.filter((message, index) => {
        if (message.role !== 'assistant' || message.error) return true
        if (turnId) return message.turnId !== turnId
        return index !== prev.length - 1
      })
      const existingIndex = turnId
        ? withoutFailedAssistant.findIndex((message) => message.error && message.turnId === turnId)
        : -1
      const card: Message = {
        key: existingIndex >= 0 ? withoutFailedAssistant[existingIndex].key : nextMessageKey('error'),
        role: 'assistant', content: '', turnId: turnId || undefined, error: true, errorSummary: summary,
        errorMessage: details.errorMessage, providerMessage: details.providerMessage, visibleText: details.visibleText,
      }
      if (existingIndex >= 0) {
        const copy = [...withoutFailedAssistant]
        const existing = copy[existingIndex]
        const keepExistingSummary = summary === 'Turn failed' && existing.errorSummary && existing.errorSummary !== 'Turn failed'
        copy[existingIndex] = {
          ...existing,
          ...card,
          errorSummary: keepExistingSummary ? existing.errorSummary : summary,
          errorMessage: details.errorMessage ?? existing.errorMessage,
          providerMessage: details.providerMessage ?? existing.providerMessage,
          visibleText: details.visibleText ?? existing.visibleText,
        }
        return copy
      }
      return [...withoutFailedAssistant, card]
    })
  }

  function consume(event: NativeEvent, turnId: string) {
    if (!nativeEventBelongsToTurn(event, turnId)) return
    const nextStream = applyNativeStreamEvent(pending.current.stream, event)
    if (nextStream !== pending.current.stream) { pending.current = { turnId, stream: nextStream }; flush(turnId) }
    if (event.kind === 'tool_use') setTool(event.tool || 'tool')
    else if (event.kind === 'result' || event.kind === 'result_error') {
      const snapshot = buildNativeUsageSnapshot(event.usage, event.model, contextWindowTokens)
      if (snapshot) onUsage(snapshot)
      setTool('')
      if (event.kind === 'result_error') {
        flushNow(turnId)
        appendTurnError(turnId, event.api_error_status ? `API error · ${event.api_error_status}` : 'Provider error', {
          providerMessage: event.text,
          visibleText: pending.current.stream.text || null,
        })
        setStatus('error')
      }
    }
    else if (event.kind === 'error') {
      flushNow(turnId)
      appendTurnError(turnId, 'Turn failed', {
        errorMessage: event.message || 'Native turn failed',
        visibleText: pending.current.stream.text || null,
      })
      setStatus('error')
    }
    else if (event.kind === 'done') {
      flushNow(turnId)
      if (event.ok === false && !turnErrorIds.current.has(turnId)) {
        appendTurnError(turnId, 'Turn failed', { visibleText: pending.current.stream.text || null })
      } else if (event.ok !== false) {
        setMessages((prev) => {
          const last = prev[prev.length - 1]
          return last?.role === 'assistant' && !last.error && !last.content && !last.thinking && !(last.recall?.length) ? prev.slice(0, -1) : prev
        })
      }
      setBusy(false); setStatus(event.ok === false ? 'error' : 'ready'); setTool('')
    }
  }

  async function send() {
    const text = input.trim()
    if (!text || busy) return
    followBottom.current = isNearBottom()
    onUserActivity(); setInput('')
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
      inputRef.current.style.overflowY = 'hidden'
    }
    setBusy(true); setStatus('busy'); setTool(''); pending.current = { turnId: '', stream: emptyNativeTurnStream() }
    const userKey = nextMessageKey('user')
    const assistantKey = nextMessageKey('assistant-live')
    setMessages((prev) => [
      ...prev,
      { key: userKey, role: 'user', content: text },
      { key: assistantKey, role: 'assistant', content: '' },
    ])
    try {
      const response = await authFetch('/api/native-chat', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' }, body: JSON.stringify({ content: text }) })
      if (!response.ok) { const data = (await response.json().catch(() => ({}))) as { error?: string }; throw new Error(data.error || `HTTP ${response.status}`) }
      const turnId = response.headers.get('x-native-turn-id') || ''
      pending.current = { turnId, stream: pending.current.stream }
      if (turnId) {
        setMessages((prev) => prev.map((message) =>
          message.key === userKey || message.key === assistantKey ? { ...message, turnId } : message,
        ))
      }
      await readSse(response, (event) => consume(event, turnId))
    } catch (error) {
      settleScheduledNativeFrameBeforeFailure(
        raf,
        pending.current.turnId,
        cancelAnimationFrame,
        commitPending,
        () => appendTurnError(pending.current.turnId, 'Network error', { errorMessage: error instanceof Error ? error.message : 'Network error', visibleText: pending.current.stream.text || null }),
      )
      setBusy(false); setStatus('error')
    }
  }

  const activityLabel = nativeActivityLabel({
    lifecycle,
    tool,
    thinking: busy && Boolean(pending.current.stream.thinking),
    busy,
    status,
  })

  return <div className={`page ${active ? 'active' : ''}`}><div className="page-inner chat-page">
    <div className="chat-status-bar"><div className={`chat-dot ${status === 'ready' || status === 'busy' ? 'live' : ''}`} /><div className="chat-name">琥珀 · Native</div><span className="chat-activity">{activityLabel}</span></div>
    <div className="chat-messages" ref={messagesRef} onScroll={handleMessagesScroll}>
      {messages.length === 0 && <div className="chat-empty"><span>🧪</span><div>实验路线 · claude -p</div></div>}
      {messages.map((m, index) => <NativeMessageRow key={m.key} message={m} streaming={busy && index === messages.length - 1 && m.role === 'assistant' && !m.error} onStreamDisplayChange={followStreamDisplay} />)}
      {tool && <div className="chat-typing active">正在使用 {tool}</div>}
    </div>
    <div className="chat-input-bar"><textarea ref={inputRef} className="chat-input" rows={1} value={input} onChange={handleInputChange} onKeyDown={(e) => { if (e.key === 'Enter' && e.metaKey) { e.preventDefault(); void send() } }} placeholder={historyReady ? "Native message…" : "恢复对话中…"} disabled={busy || !historyReady} /><button type="button" className="chat-send" onClick={() => void send()} disabled={!input.trim() || busy || !historyReady}>↑</button></div>
  </div></div>
}
